import { useEffect, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getRmData, Deactivate } from "../../../redux/slices/rmSlice";
import CustomTable from "../../../components/CustomTable/CustomTable";
import CustomModal from "../../../components/CustomModal/CustomModal";
import CustomCard from "../../../components/CustomCard/CustomCard";
import TableHeader from "../../../components/CustomTable/TableHeader"
import useSearchableTable from "../../../hooks/useSearchableTable";
import { TITLE_TXT, BTN_TXT, MODAL_WIDTHS, DESC_TXT } from "../../../constants/constants";
import AssignRmForm from "./Components/AssignRmForm";
import { getRmColumns } from "./Components/getRmColumns";
import InfoModal from "../../../components/CustomModal/InfoModal";
import { Flex, Typography } from "antd";
import { CloseCircleFilled } from "@ant-design/icons";
import ReasonForm from "../../../components/CustomForms/ReasonForm";
import {
  TABLE_COLUMN_DEFAULTS,
  buildTableScreenKey,
} from "../../../constants/tableColumnDefaults";
import "./ManageRm.scss";

const { Text } = Typography;

const ManageRm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { rmTabledata, loading, deactivateRmLoading, error } = useSelector((state) => state?.rm);
  const [modalState, setModalState] = useState({
    type: null, // "assignRmModal" | "deactivateWithUsersModal" | "deactivateWithoutUsersModal"
    data: null, // hold rm info
  });
  const [columnControl, setColumnControl] = useState(null);

  useEffect(() => {
    dispatch(getRmData());
  }, [dispatch]);

  const { searchText, setSearchText, filteredData } = useSearchableTable(rmTabledata);

  const closeModal = () => setModalState({ type: null, data: null });

  const handleAssignRm = () => {
    setModalState({ type: "assignRmModal", data: null });
  };

  const handleView = useCallback((rm) => navigate("/user-role-management/manage-rm/rm-details", { state: { rm } }), [navigate]);

  const handleDeactivateClick = useCallback((rm) => {
    if (rm.usersAssigned !== 0) {
      setModalState({ type: "deactivateWithUsersModal", data: rm });
    } else {
      setModalState({ type: "deactivateWithoutUsersModal", data: rm });
    }
  }, []);

  const handleDeactivateRm = async (values) => {
    const payload = {
      rmUserId: modalState.data.userId,
      reason: values.reason
    }
    const res = await dispatch(Deactivate(payload));
    if (res.payload.statusCode === 200) {
      closeModal();
      dispatch(getRmData());
    }
  }

  const columns = useMemo(
    () =>
      getRmColumns({
        onView: handleView,
        onDeactivate: handleDeactivateClick,
      }),
    [handleView, handleDeactivateClick]
  );

  return (
    <CustomCard>
      <TableHeader
        title={TITLE_TXT.RM_LIST}
        searchText={searchText}
        setSearchText={setSearchText}
        handleBtnOnClick={handleAssignRm}
        btnName={BTN_TXT.ASSIGN_RM}
        columnControl={columnControl}
      />
      <CustomTable
        rowKey="userId"
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        screenKey={buildTableScreenKey("manageRm")}
        defaultColumnKeys={TABLE_COLUMN_DEFAULTS.manageRm}
        onColumnControlChange={setColumnControl}
      />

      {modalState.type === "assignRmModal" && (
        <CustomModal
          title={TITLE_TXT.ASSIGN_RM}
          open
          centered
          width={MODAL_WIDTHS}
          content={<AssignRmForm onClose={closeModal} />}
        />
      )}

      {modalState.type === "deactivateWithoutUsersModal" && (
        <CustomModal
          title={TITLE_TXT.DEACTIVATE_CONFIRM}
          open
          centered
          content={
            <ReasonForm
              label="Reason for Deactivation"
              placeholder="Write a valid Reason for deactivating User"
              btnName="Deactivate"
              submitLoading={deactivateRmLoading}
              submitError={error}
              onClose={closeModal}
              handleOnFormSubmit={handleDeactivateRm}
            />
          }
        />
      )}

      {modalState.type === "deactivateWithUsersModal" && (
        <InfoModal
          title={`Deactivate Failed for ${modalState.data?.userName} (${modalState.data?.userId})`}
          open
          onCancel={closeModal}
          centered
          content={
            <Flex align="flex-start" gap={15} style={{ marginTop: 20 }}>
              <CloseCircleFilled style={{ fontSize: 36, color: "#EE5353" }} />
              <Text style={{ color: "#464646", fontSize: 14 }}>
                {DESC_TXT.DEACTIVATE_RM}
              </Text>
            </Flex>
          }
        />
      )}
    </CustomCard>
  );
};

export default ManageRm;
